import React, { useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Card,
    CardContent,
    Tabs,
    Tab,
    Chip,
    LinearProgress
} from '@mui/material';
import {
    PieChart as PieChartIcon,
    TrendingUp as TrendingIcon,
    Flight as FlightIcon,
    Luggage as TourismeIcon
} from '@mui/icons-material';
import { formatCurrency } from '../../../utils/dashboardAnalyticsEngine';

const DashboardChartsView = ({
    periods = [],
    periodData = [],
    allCompagnies = [],
    allClients = []
}) => {
    const [tabIndex, setTabIndex] = useState(0);

    // Calcul des totaux cumulés pour les 4 périodes
    const totalCABilletterie = periodData.reduce((sum, p) => sum + (p.billetterie?.ca || 0), 0);
    const totalCATourisme = periodData.reduce((sum, p) => sum + (p.tourisme?.ca || 0), 0);
    const totalCAGlobal = totalCABilletterie + totalCATourisme;
    const totalMargeGlobale = periodData.reduce((sum, p) => sum + (p.global?.marge || 0), 0);

    // Données d'évolution par période
    const maxPeriodCA = Math.max(1, ...periodData.map(p => p.global?.ca || 0));

    return (
        <Box sx={{ width: '100%' }}>
            {/* Cartes KPI récapitulatives en haut */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: '#0f172a', color: 'white', borderRadius: 2 }}>
                        <CardContent sx={{ p: 2 }}>
                            <Typography variant="caption" sx={{ color: '#94a3b8', textTransform: 'uppercase' }}>
                                CA Total (4 Périodes)
                            </Typography>
                            <Typography variant="h5" fontWeight="bold" sx={{ my: 0.5, color: '#38bdf8' }}>
                                {formatCurrency(totalCAGlobal)} F CFA
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#cbd5e1' }}>
                                Billetterie + Tourisme
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: '#1e293b', color: 'white', borderRadius: 2 }}>
                        <CardContent sx={{ p: 2 }}>
                            <Typography variant="caption" sx={{ color: '#94a3b8', textTransform: 'uppercase' }}>
                                Marge Globale
                            </Typography>
                            <Typography variant="h5" fontWeight="bold" sx={{ my: 0.5, color: '#4ade80' }}>
                                {formatCurrency(totalMargeGlobale)} F CFA
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#cbd5e1' }}>
                                {totalCAGlobal > 0 ? ((totalMargeGlobale / totalCAGlobal) * 100).toFixed(1) : 0} % du Chiffre d'Affaires
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: 'white', border: '1px solid #e2e8f0', borderRadius: 2 }}>
                        <CardContent sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <FlightIcon color="primary" fontSize="small" />
                                <Typography variant="caption" color="text.secondary" textTransform="uppercase">
                                    Part Billetterie
                                </Typography>
                            </Box>
                            <Typography variant="h5" fontWeight="bold" sx={{ my: 0.5, color: '#0ea5e9' }}>
                                {formatCurrency(totalCABilletterie)} F CFA
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {totalCAGlobal > 0 ? ((totalCABilletterie / totalCAGlobal) * 100).toFixed(1) : 0} % du Total
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: 'white', border: '1px solid #e2e8f0', borderRadius: 2 }}>
                        <CardContent sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TourismeIcon color="secondary" fontSize="small" />
                                <Typography variant="caption" color="text.secondary" textTransform="uppercase">
                                    Part Tourisme
                                </Typography>
                            </Box>
                            <Typography variant="h5" fontWeight="bold" sx={{ my: 0.5, color: '#8b5cf6' }}>
                                {formatCurrency(totalCATourisme)} F CFA
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {totalCAGlobal > 0 ? ((totalCATourisme / totalCAGlobal) * 100).toFixed(1) : 0} % du Total
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Onglets des graphiques */}
            <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden', mb: 3 }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#f8fafc', px: 2 }}>
                    <Tabs value={tabIndex} onChange={(e, v) => setTabIndex(v)}>
                        <Tab icon={<TrendingIcon />} iconPosition="start" label="Évolution sur 4 périodes" sx={{ textTransform: 'none' }} />
                        <Tab icon={<PieChartIcon />} iconPosition="start" label="Répartition Billetterie / Tourisme" sx={{ textTransform: 'none' }} />
                        <Tab icon={<FlightIcon />} iconPosition="start" label="Compagnies Aériennes" sx={{ textTransform: 'none' }} />
                        <Tab icon={<TourismeIcon />} iconPosition="start" label="Prestations Tourisme" sx={{ textTransform: 'none' }} />
                    </Tabs>
                </Box>

                <Box sx={{ p: 3 }}>
                    {/* ONGLET 0 : ÉVOLUTION SUR LES 4 PÉRIODES */}
                    {tabIndex === 0 && (
                        <Box>
                            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                                Comparaison du Chiffre d'Affaires et des Marges sur les 4 Périodes
                            </Typography>
                            <Grid container spacing={3}>
                                {periodData.map((p, idx) => {
                                    const ca = p.global?.ca || 0;
                                    const marge = p.global?.marge || 0;
                                    const caBillet = p.billetterie?.ca || 0;
                                    const caTour = p.tourisme?.ca || 0;
                                    const pctHeight = Math.max(8, (ca / maxPeriodCA) * 100);

                                    return (
                                        <Grid item xs={12} sm={6} md={3} key={idx}>
                                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, textAlign: 'center', height: '100%' }}>
                                                <Chip 
                                                    label={p.period.shortLabel} 
                                                    color={p.isEmpty ? 'default' : 'primary'} 
                                                    size="small" 
                                                    sx={{ mb: 1.5, fontWeight: 'bold' }} 
                                                />
                                                <Typography variant="caption" display="block" color="text.secondary" sx={{ minHeight: 32 }}>
                                                    {p.period.label.replace('\n', ' ')}
                                                </Typography>

                                                {p.isEmpty ? (
                                                    <Box sx={{ py: 6, color: 'text.disabled' }}>
                                                        <Typography variant="body2" fontStyle="italic">
                                                            Période future non échue
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Box sx={{ mt: 2 }}>
                                                        {/* Barre visuelle relative */}
                                                        <Box sx={{ height: 120, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 1, mb: 2 }}>
                                                            {/* Barre CA Total */}
                                                            <Box sx={{ 
                                                                width: 36, 
                                                                height: `${pctHeight}%`, 
                                                                bgcolor: '#0ea5e9', 
                                                                borderRadius: '4px 4px 0 0',
                                                                transition: 'height 0.3s ease'
                                                            }} />
                                                            {/* Barre Marge */}
                                                            <Box sx={{ 
                                                                width: 24, 
                                                                height: `${Math.max(4, (marge / maxPeriodCA) * 100)}%`, 
                                                                bgcolor: '#10b981', 
                                                                borderRadius: '4px 4px 0 0',
                                                                transition: 'height 0.3s ease'
                                                            }} />
                                                        </Box>

                                                        <Typography variant="subtitle2" fontWeight="bold">
                                                            CA : {formatCurrency(ca)} F
                                                        </Typography>
                                                        <Typography variant="caption" color="success.main" fontWeight="bold" display="block">
                                                            Marge : {formatCurrency(marge)} F ({ca > 0 ? ((marge / ca) * 100).toFixed(1) : 0}%)
                                                        </Typography>
                                                        
                                                        <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px dashed #e2e8f0', fontSize: '0.75rem', color: 'text.secondary' }}>
                                                            <div>Billetterie : {formatCurrency(caBillet)} F</div>
                                                            <div>Tourisme : {formatCurrency(caTour)} F</div>
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Paper>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Box>
                    )}

                    {/* ONGLET 1 : RÉPARTITION ACTIVITÉ */}
                    {tabIndex === 1 && (
                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                                    Structure Globale du Chiffre d'Affaires
                                </Typography>
                                <Box sx={{ mb: 3 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography variant="body2" fontWeight="600">Billetterie ({totalCAGlobal > 0 ? ((totalCABilletterie / totalCAGlobal) * 100).toFixed(1) : 0} %)</Typography>
                                        <Typography variant="body2" fontWeight="bold">{formatCurrency(totalCABilletterie)} F CFA</Typography>
                                    </Box>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={totalCAGlobal > 0 ? (totalCABilletterie / totalCAGlobal) * 100 : 0} 
                                        sx={{ height: 12, borderRadius: 2, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: '#0ea5e9' } }}
                                    />
                                </Box>

                                <Box sx={{ mb: 3 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography variant="body2" fontWeight="600">Tourisme ({totalCAGlobal > 0 ? ((totalCATourisme / totalCAGlobal) * 100).toFixed(1) : 0} %)</Typography>
                                        <Typography variant="body2" fontWeight="bold">{formatCurrency(totalCATourisme)} F CFA</Typography>
                                    </Box>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={totalCAGlobal > 0 ? (totalCATourisme / totalCAGlobal) * 100 : 0} 
                                        sx={{ height: 12, borderRadius: 2, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: '#8b5cf6' } }}
                                    />
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, bgcolor: '#f8fafc' }}>
                                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                                        Analyse de la Contribution
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" paragraph>
                                        Ce graphique met en regard la part respective de chaque branche d'activité sur le chiffre d'affaires total de l'agence.
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <Chip label={`Total CA : ${formatCurrency(totalCAGlobal)} F`} color="primary" />
                                        <Chip label={`Marge Globale : ${formatCurrency(totalMargeGlobale)} F`} color="success" />
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>
                    )}

                    {/* ONGLET 2 : COMPAGNIES AÉRIENNES */}
                    {tabIndex === 2 && (
                        <Box>
                            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                                Répartition des Ventes de Billetterie par Compagnie
                            </Typography>
                            <Grid container spacing={2}>
                                {allCompagnies.map((comp, idx) => {
                                    const totalCompCA = periodData.reduce((sum, p) => sum + (p.compagnies?.[comp] || 0), 0);
                                    const compPct = totalCABilletterie > 0 ? (totalCompCA / totalCABilletterie) * 100 : 0;

                                    return (
                                        <Grid item xs={12} sm={6} md={4} key={idx}>
                                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                    <Typography variant="subtitle2" fontWeight="bold">{comp}</Typography>
                                                    <Chip label={`${compPct.toFixed(1)} %`} size="small" color="primary" variant="outlined" />
                                                </Box>
                                                <Typography variant="h6" color="primary.main" fontWeight="bold">
                                                    {formatCurrency(totalCompCA)} F
                                                </Typography>
                                                <LinearProgress 
                                                    variant="determinate" 
                                                    value={compPct} 
                                                    sx={{ mt: 1, height: 6, borderRadius: 1 }} 
                                                />
                                            </Paper>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Box>
                    )}

                    {/* ONGLET 3 : PRESTATIONS TOURISME */}
                    {tabIndex === 3 && (
                        <Box>
                            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                                Ventilation par Prestation Tourisme (Circuits, Excursions, Transferts, Séjours)
                            </Typography>
                            <Grid container spacing={2}>
                                {[
                                    { key: 'circuits', label: 'Circuits', color: '#0ea5e9' },
                                    { key: 'excursions', label: 'Excursions', color: '#10b981' },
                                    { key: 'transferts', label: 'Transferts', color: '#f59e0b' },
                                    { key: 'sejours', label: 'Séjours', color: '#8b5cf6' }
                                ].map((prest, idx) => {
                                    const caPrest = periodData.reduce((sum, p) => sum + (p.prestations?.[prest.key]?.ca || 0), 0);
                                    const debPrest = periodData.reduce((sum, p) => sum + (p.prestations?.[prest.key]?.debours || 0), 0);
                                    const margePrest = caPrest - debPrest;
                                    const pct = totalCATourisme > 0 ? (caPrest / totalCATourisme) * 100 : 0;

                                    return (
                                        <Grid item xs={12} sm={6} md={3} key={idx}>
                                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderTop: `4px solid ${prest.color}` }}>
                                                <Typography variant="subtitle1" fontWeight="bold">{prest.label}</Typography>
                                                <Typography variant="h6" fontWeight="bold" sx={{ my: 0.5, color: prest.color }}>
                                                    {formatCurrency(caPrest)} F
                                                </Typography>
                                                <Typography variant="caption" display="block" color="text.secondary">
                                                    Part CA : {pct.toFixed(1)} %
                                                </Typography>
                                                <Typography variant="caption" display="block" color="success.main" fontWeight="bold">
                                                    Marge : {formatCurrency(margePrest)} F ({caPrest > 0 ? ((margePrest / caPrest) * 100).toFixed(1) : 0}%)
                                                </Typography>
                                                <LinearProgress 
                                                    variant="determinate" 
                                                    value={pct} 
                                                    sx={{ mt: 1.5, height: 6, borderRadius: 1 }} 
                                                />
                                            </Paper>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Box>
                    )}
                </Box>
            </Paper>
        </Box>
    );
};

export default DashboardChartsView;
